"use client";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons/faChevronDown";
import { faChevronUp } from "@fortawesome/pro-solid-svg-icons/faChevronUp";
import { faClock } from "@fortawesome/pro-solid-svg-icons/faClock";
import { cn } from "@wondermaps/ui";
import type { HoursConfig, DaySchedule } from "@wondermaps/db";
import {
  isCurrentlyOpen,
  getTodaySchedule,
  formatTime,
} from "@wondermaps/maps";

interface HoursDisplayProps {
  hours: HoursConfig | null;
}

/**
 * Displays POI operating hours for visitors.
 * Shows today's hours prominently with expandable full week view.
 */
export function HoursDisplay({ hours }: HoursDisplayProps) {
  const { t } = useTranslation("pois");
  const [isExpanded, setIsExpanded] = useState(false);

  if (!hours) {
    return null;
  }

  // Always open
  if (hours.type === "always_open") {
    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faClock} className="h-4 w-4 text-emerald-600" aria-hidden="true" />
          <span className="text-sm font-medium text-emerald-600">{t("hours.open24_7")}</span>
        </div>
        {hours.note && (
          <p className="text-xs text-muted-foreground">{hours.note}</p>
        )}
      </div>
    );
  }

  // Hours vary
  if (hours.type === "variable") {
    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faClock} className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <span className="text-sm text-muted-foreground">{t("hours.hoursVary")}</span>
        </div>
        {hours.note && (
          <p className="text-xs text-muted-foreground">{hours.note}</p>
        )}
      </div>
    );
  }

  // Scheduled hours
  if (hours.type === "scheduled" && hours.schedule) {
    const todaySchedule = getTodaySchedule(hours.schedule);
    const currentlyOpen = isCurrentlyOpen(hours);
    const today = new Date().getDay() as DaySchedule["day"];

    return (
      <div className="flex flex-col gap-2">
        {/* Today's hours */}
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center w-full text-left"
          aria-expanded={isExpanded}
          aria-label={t("hours.todayHours", {
            hours: todaySchedule?.closed
              ? t("hours.closed")
              : `${formatTime(todaySchedule?.open ?? "")} - ${formatTime(todaySchedule?.close ?? "")}`,
            action: isExpanded ? t("hours.collapse") : t("hours.expand"),
          })}
        >
          <div className="flex items-center gap-2">
            <FontAwesomeIcon
              icon={faClock}
              className={cn(
                "h-4 w-4 shrink-0",
                currentlyOpen ? "text-emerald-600" : "text-red-600"
              )}
              aria-hidden="true"
            />
            <span
              className={cn(
                "text-sm font-medium",
                currentlyOpen ? "text-emerald-600" : "text-red-600"
              )}
            >
              {currentlyOpen ? t("hours.openNow") : t("hours.closed")}
            </span>
          </div>
          <div className="flex items-center gap-1.5 ml-auto">
            {!todaySchedule?.closed && (
              <span className="text-sm text-muted-foreground">
                {`${formatTime(todaySchedule?.open ?? "")} – ${formatTime(todaySchedule?.close ?? "")}`}
              </span>
            )}
            <FontAwesomeIcon
              icon={isExpanded ? faChevronUp : faChevronDown}
              className="h-3.5 w-3.5 text-muted-foreground"
              aria-hidden="true"
            />
          </div>
        </button>

        {/* Expanded week schedule */}
        {isExpanded && (
          <div
            className="bg-foreground/[0.04] rounded-2xl p-3 divide-y divide-border mt-1"
            role="list"
            aria-label={t("hours.weeklySchedule")}
          >
            {hours.schedule.map((daySchedule) => (
              <div
                key={daySchedule.day}
                className={cn(
                  "flex justify-between py-1.5 text-sm",
                  daySchedule.day === today
                    ? "font-medium"
                    : "text-muted-foreground"
                )}
                role="listitem"
              >
                <span>{t(`days.full.${daySchedule.day}`)}</span>
                <time
                  dateTime={
                    daySchedule.closed
                      ? undefined
                      : `${daySchedule.open}/${daySchedule.close}`
                  }
                >
                  {daySchedule.closed
                    ? t("hours.closed")
                    : `${formatTime(daySchedule.open)} – ${formatTime(daySchedule.close)}`}
                </time>
              </div>
            ))}
          </div>
        )}

        {/* Note */}
        {hours.note && (
          <p className="text-xs text-muted-foreground">{hours.note}</p>
        )}
      </div>
    );
  }

  return null;
}
